import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function PasswordResetRequest() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password-reset/request", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#EBE5DF]">
      <div className="hidden md:flex flex-col justify-between bg-[#0B2230] text-[#EBE5DF] p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "url(https://images.unsplash.com/photo-1648775507324-b48dd3791fa5?w=1200)",
                      backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center">
            <Stethoscope className="w-6 h-6" />
          </div>
          <div>
            <div className="font-heading font-semibold text-2xl text-white">Clínica Kloss</div>
            <div className="text-xs uppercase tracking-widest text-white/60">Sistema de Estoque & Protocolos</div>
          </div>
        </div>
        <div className="relative z-10 space-y-3">
          <h1 className="font-heading font-bold text-4xl leading-tight text-white">Recupere seu acesso.</h1>
          <p className="text-white/70 max-w-md">Enviaremos um link seguro para o seu e-mail. Ele expira em 2 horas e pode ser usado apenas uma vez.</p>
        </div>
        <div className="relative z-10 text-xs text-white/40 uppercase tracking-widest">Uso restrito · LGPD</div>
      </div>

      <div className="flex items-center justify-center p-8">
        {sent ? (
          <div className="w-full max-w-sm space-y-6" data-testid="reset-request-sent">
            <div className="flex items-center gap-3 text-[#065F46]">
              <CheckCircle2 className="w-8 h-8" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#8C98A0]">E-mail enviado</div>
                <h2 className="font-heading text-2xl font-semibold text-[#0B2230] mt-1">Verifique sua caixa de entrada</h2>
              </div>
            </div>
            <p className="text-sm text-[#5C6B73]">Se <strong>{email}</strong> estiver cadastrado, você receberá um link para redefinir a sua senha em alguns instantes.</p>
            <div className="text-xs text-[#8C98A0] bg-white/60 border border-[#DCD7CE] rounded-lg p-3">
              Não recebeu? Confira a pasta de spam ou tente novamente em alguns minutos. Se persistir, fale com o administrador da clínica.
            </div>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#0B2230] hover:underline" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Voltar para o login
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="reset-request-form">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#8C98A0]">Redefinir senha</div>
              <h2 className="font-heading text-3xl font-semibold text-[#0B2230] mt-1">Esqueci minha senha</h2>
              <p className="text-sm text-[#5C6B73] mt-2">Informe o e-mail cadastrado. Enviaremos um link para você criar uma nova senha.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6B73] font-medium">E-mail</label>
              <input
                data-testid="reset-email"
                type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 px-4 py-3 rounded-lg border border-[#DCD7CE] bg-white focus:outline-none focus:ring-2 focus:ring-[#0B2230]/20"
              />
            </div>
            <button
              data-testid="reset-request-submit"
              type="submit" disabled={busy}
              className="w-full bg-[#0B2230] hover:bg-[#143346] disabled:opacity-60 text-white font-medium py-3 rounded-lg transition flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Enviar link
            </button>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#5C6B73] hover:text-[#0B2230] hover:underline">
              <ArrowLeft className="w-4 h-4" /> Voltar para o login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
}
